import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { eventTypeInput, eventTypePatch, leadSourceInput, leadSourcePatch } from "@shared/schemas/catalog";
import { validateEventType, validateLeadSource } from "@shared/validation/admin";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { recordValidationQuery, useBatchValidation } from "@web/lib/validation";
import { api, eventTypesQuery, leadSourcesQuery, type EventType, type LeadSource } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, EmptyState, Inline, Page, Row, RowGroup, Skeleton, Stack, StatusChip, TextField, Toggle, UnsavedChanges, ValidationBadge, ValidationSummary, useToast } from "@web/ui";
import { AdminsOnly } from "./Settings";

type Named = { id: string; name: string; active: number; sort: number };

type ListProps<T extends Named> = {
  entity: "event_type" | "lead_source";
  rows: T[] | undefined;
  /** The list query's failure, so a dead query says so instead of shimmering. */
  failure: string | null;
  onRetry: () => void;
  create: (body: { name: string }) => Promise<unknown>;
  patch: (id: string, body: Record<string, unknown>) => Promise<unknown>;
  subtitle?: (r: T) => ReactNode;
  extra?: (r: T, set: (body: Record<string, unknown>, patch: Partial<T>) => void, disabled: boolean) => ReactNode;
  placeholder: string;
};

/** Shared list editor: add by name; all sheet changes remain a draft until Save. */
function NamedList<T extends Named>({ entity, rows, failure, onRetry, create, patch, subtitle, extra, placeholder }: ListProps<T>) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [editing, setEditing] = useState<T | null>(null);
  const [original, setOriginal] = useState<T | null>(null);
  const [editName, setEditName] = useState("");
  const [editSort, setEditSort] = useState("0");

  const saved = useQuery(recordValidationQuery(entity, editing?.id ?? ""));
  const checks = useBatchValidation((rows ?? []).map(row => ({ entityType: entity, id: row.id })));
  const body = editing && original ? changedValues({ ...editing, name: editName.trim(), sort: Number(editSort) }, original) : { name: name.trim() };
  const parsed = parseInput(editing ? entity === "event_type" ? eventTypePatch : leadSourcePatch : entity === "event_type" ? eventTypeInput : leadSourceInput, body, { type: entity, id: editing?.id }, `${entity}.save`);
  const pure = (entity === "event_type" ? validateEventType : validateLeadSource)({ ...original, ...body }, { before: original ?? undefined, changedFields: Object.keys(body) });
  const validation = useFormValidation(evaluation([...parsed.issues, ...pure.issues], false));
  const { busy, field } = validation;

  async function add() {
    if (!parsed.success || !validation.begin(`${entity}.save`)) return;
    try {
      await create({ name: name.trim() });
      setName("");
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }
  async function saveEdit() {
    if (!editing || !parsed.success || !validation.begin(`${entity}.save`)) return;
    try {
      await patch(editing.id, parsed.data);
      setEditing(null);
      setOriginal(null);
      validation.clear();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <Page>
      <UnsavedChanges dirty={!busy && (editing ? Object.keys(body).length > 0 : Boolean(name))} />
      <Stack gap={3}>
        {(checks.isError || checks.data?.checksComplete === false) && <ValidationSummary result={checks.data} unavailable onRetry={() => void checks.refetch()} />}
        <Inline gap={2} align="end">
          <TextField label="Add" className="flex-1" placeholder={placeholder} value={name} onChange={(e) => validation.edit(() => setName(e.target.value))} onKeyDown={(e) => e.key === "Enter" && add()} {...(!editing ? field("name") : { disabled: busy })} />
          <Button fullWidth={false} loading={busy} icon={<Plus aria-hidden size={18} />} onClick={add}>
            Add
          </Button>
        </Inline>
        {!editing && <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />}
        {failure ? (
          <EmptyState title={`${entity === "event_type" ? "Event types" : "Lead sources"} could not be loaded`} body={failure} action={<Button onClick={onRetry}>Try again</Button>} />
        ) : !rows ? (
          <Skeleton count={5} />
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing here yet" />
        ) : (
          <RowGroup>
            {rows.map((r) => (
              <Row
                key={r.id}
                title={r.name}
                disabled={busy}
                subtitle={subtitle?.(r)}
                onClick={() => validation.edit(() => {
                  setEditing(r);
                  setOriginal(r);
                  setEditName(r.name);
                  setEditSort(String(r.sort));
                })}
                trailing={<Stack gap={1}>{r.active ? <StatusChip tone="success">Active</StatusChip> : <StatusChip>Inactive</StatusChip>}{checks.isFetching && !checks.data ? <StatusChip>Checking…</StatusChip> : <ValidationBadge result={checks.data?.records.find(result => result.entity.type === entity && result.entity.id === r.id)} unavailable={checks.isError} />}</Stack>}
              />
            ))}
          </RowGroup>
        )}
      </Stack>
      <BottomSheet
        open={Boolean(editing)}
        onClose={() => validation.edit(() => { setEditing(null); setOriginal(null); })}
        title="Edit"
        footer={
          <Button loading={busy} onClick={saveEdit}>
            Save
          </Button>
        }
      >
        {editing && (
          <Stack gap={3}>
            <ValidationSummary result={saved.data} loading={saved.isFetching} unavailable={saved.isError} onRetry={() => void saved.refetch()} saved />
            <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
            <TextField label="Name" value={editName} onChange={(e) => validation.edit(() => setEditName(e.target.value))} autoFocus {...field("name")} />
            <TextField label="Sort" type="number" inputMode="numeric" value={editSort} onChange={(e) => validation.edit(() => setEditSort(e.target.value))} {...field("sort")} />
            <Toggle label="Active" checked={Boolean(editing.active)} disabled={busy} onChange={(v) => validation.edit(() => setEditing({ ...editing, active: v ? 1 : 0 }))} />
            {extra?.(editing, (_body, opt) => validation.edit(() => setEditing({ ...editing, ...opt })), busy)}
          </Stack>
        )}
      </BottomSheet>
    </Page>
  );
}

export function SettingsEventTypes() {
  const me = useMe();
  const q = useQuery({ ...eventTypesQuery(true), enabled: me.companyAdmin });
  if (!me.companyAdmin) return <AdminsOnly />;
  return <NamedList<EventType> entity="event_type" rows={q.data} failure={q.isError ? errorMessage(q.error) : null} onRetry={() => void q.refetch()} create={api.createEventType} patch={api.patchEventType} placeholder="e.g. Wedding" />;
}

export function SettingsLeadSources() {
  const me = useMe();
  const q = useQuery({ ...leadSourcesQuery(true), enabled: me.companyAdmin });
  if (!me.companyAdmin) return <AdminsOnly />;
  return (
    <NamedList<LeadSource>
      entity="lead_source"
      rows={q.data}
      failure={q.isError ? errorMessage(q.error) : null}
      onRetry={() => void q.refetch()}
      create={api.createLeadSource}
      patch={api.patchLeadSource}
      placeholder="e.g. Eventective"
      subtitle={(r) => (r.autoAck ? "Auto-acknowledges new leads" : "No auto-acknowledgment")}
      extra={(r, set, disabled) => <Toggle label="Auto-ack" checked={Boolean(r.autoAck)} disabled={disabled} onChange={(v) => set({ autoAck: v }, { autoAck: v ? 1 : 0 })} />}
    />
  );
}
